import { useEffect, useState, type FormEvent } from 'react'
import { Navigate, useSearchParams } from 'react-router'
import { useSession } from '../lib/session'
import { setBackendOverride } from '../lib/env'
import { btn, errorText, inputCls, labelCls } from '../components/ui'
import { readQueue } from '../lib/takes'

/** Only same-app paths, never "//evil.example". */
function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/'
}

export function SignInPage() {
  const { backend, viewer, loading } = useSession()
  const [params] = useSearchParams()
  const next = safeNext(params.get('next'))
  const [mode, setMode] = useState<'password' | 'code'>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Lines taken on a writing while signed out; the reader saves them once we're back.
  const [waiting] = useState(() => readQueue().length)

  useEffect(() => {
    document.title = 'Sign in — Burrow Writings'
  }, [])

  if (!loading && viewer) return <Navigate to={next} replace />

  const run = (fn: () => Promise<void>) => async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center px-4 py-12">
      <div className="flex w-full max-w-[400px] flex-col gap-6">
        <div className="flex flex-col gap-2 text-center">
          <span className="font-display text-[1.3rem] font-medium italic text-primary">Burrow</span>
          <h1 className="m-0 font-display text-[32px]/[1.1] font-normal text-primary">Sign in to Writings</h1>
          <p className="m-0 text-[13.5px]/[1.5] text-muted-foreground">Use your Burrow account. Writings runs against the dev database while it’s being built.</p>
          {waiting > 0 && (
            <p role="status" className="m-0 mt-1 rounded-lg bg-accent/10 px-3 py-2 text-[13px]/[1.45] font-medium text-primary">
              Sign in and what you took ({waiting}) goes into your thoughts, loose, for you to sort.
            </p>
          )}
        </div>

        {mode === 'password' ? (
          <form onSubmit={run(() => backend.signInWithPassword(email.trim(), password))} className="flex flex-col gap-3.5 rounded-2xl border border-card-edge bg-card p-6 shadow-[0_1px_3px_rgba(45,27,30,.06)]">
            <label className="flex flex-col gap-1.5">
              <span className={labelCls}>EMAIL</span>
              <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className={labelCls}>PASSWORD</span>
              <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            </label>
            <button type="submit" disabled={busy} className={`${btn.primaryLg}`}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            <button type="button" onClick={() => setMode('code')} className="text-[13px] font-medium text-primary hover:underline">
              Email me a sign-in code instead
            </button>
          </form>
        ) : (
          <form
            onSubmit={run(async () => {
              if (!codeSent) {
                await backend.sendEmailCode(email.trim())
                setCodeSent(true)
              } else {
                await backend.verifyEmailCode(email.trim(), code.trim())
              }
            })}
            className="flex flex-col gap-3.5 rounded-2xl border border-card-edge bg-card p-6 shadow-[0_1px_3px_rgba(45,27,30,.06)]"
          >
            <label className="flex flex-col gap-1.5">
              <span className={labelCls}>EMAIL</span>
              <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} disabled={codeSent} className={inputCls} />
            </label>
            {codeSent && (
              <label className="flex flex-col gap-1.5">
                <span className={labelCls}>CODE FROM YOUR EMAIL</span>
                <input inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} className={inputCls} />
              </label>
            )}
            <button type="submit" disabled={busy} className={`${btn.primaryLg}`}>
              {busy ? 'One moment…' : codeSent ? 'Sign in' : 'Send code'}
            </button>
            <button type="button" onClick={() => setMode('password')} className="text-[13px] font-medium text-primary hover:underline">
              Use a password instead
            </button>
          </form>
        )}

        {error && (
          <p role="alert" className="m-0 text-center text-[13px] font-medium text-primary">
            {error}
          </p>
        )}

        <p className="m-0 text-center text-[13px] text-muted-foreground">
          Dev database unavailable?{' '}
          <button
            type="button"
            onClick={() => {
              setBackendOverride('local')
              window.location.assign('/')
            }}
            className="font-medium text-muted-foreground underline"
          >
            Use local demo data
          </button>
        </p>
      </div>
    </main>
  )
}
